import { useEffect, useRef } from 'react';
import { Notification } from '../Notification/notification';
import { useError } from '@/core/hooks/useError';
import { addPeriod } from '@/shared/utils/addPeriod';

const DEDUP_WINDOW_MS = 2000;

interface ShownCode {
  key: string;
  timestamp: number;
}

export function ErrorDialog() {
  const { currentError, clearError: clearDialogError } = useError();
  const shownIdsRef = useRef<Set<string>>(new Set());
  const recentCodesRef = useRef<ShownCode[]>([]);

  useEffect(() => {
    if (!currentError) return;
    if (currentError.severity !== 'error' && currentError.severity !== 'warning') return;

    // ID-level dedup: never show the exact same error object twice
    if (shownIdsRef.current.has(currentError.id)) return;
    shownIdsRef.current.add(currentError.id);

    // Code-level dedup: skip if same errorCode shown within the dedup window
    const dedupKey = currentError.errorCode ?? currentError.title;
    const now = Date.now();
    const isDuplicate = recentCodesRef.current.some(
      (e) => e.key === dedupKey && now - e.timestamp < DEDUP_WINDOW_MS,
    );
    if (isDuplicate) {
      clearDialogError(currentError.id);
      return;
    }
    recentCodesRef.current.push({ key: dedupKey, timestamp: now });

    const notify = currentError.severity === 'warning' ? Notification.warning : Notification.error;
    notify({
      title: currentError.title,
      description: addPeriod(currentError.message),
      duration: 3000,
      onDismiss: () => clearDialogError(currentError.id),
    });

    const timeoutId = setTimeout(() => {
      clearDialogError(currentError.id);
    }, 100);

    return () => clearTimeout(timeoutId);
    // react-doctor-disable-next-line react-doctor/exhaustive-deps -- stable refs/functions; including them would not change behavior
  }, [currentError?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prune stale dedup codes to prevent unbounded growth
  useEffect(() => {
    const cleanup = setInterval(() => {
      const cutoff = Date.now() - DEDUP_WINDOW_MS * 10;
      recentCodesRef.current = recentCodesRef.current.filter((e) => e.timestamp > cutoff);
      if (shownIdsRef.current.size > 200) shownIdsRef.current.clear();
    }, 30000);
    return () => clearInterval(cleanup);
  }, []);

  return null;
}
